import { Link } from '@reach/router';
import React, { useState } from 'react';
import styled, { css } from 'styled-components/macro';
import {
  focusRing,
  fontSize,
  fontWeight,
  lineHeight,
  radius,
  space,
} from '../../../theme/tokens';
import * as StaffCommon from '../../common/elements/Staff';

export const ModuleCard = styled(StaffCommon.StaffCard)`
 ${({ isEditing, groupChatId }) =>
   (isEditing || groupChatId) &&
   css`
     display: flex;
     justify-content: space-between;
     gap: ${space[3]};
   `}
  ${({ isDragging }) =>
    isDragging &&
    css`
      opacity: 0.3;
    `}
  ${({ isOver, theme }) =>
    isOver &&
    css`
      background-color: ${theme.hoverFill};
    `}
`;

export const ModuleLink = styled(Link)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${space[3]};
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  text-decoration: none;
  color: inherit;
  border-radius: ${radius.md};
  &[data-isediting='true'] {
    &:hover {
      cursor: default;
    }
  }
  ${focusRing}
`;

export const ModuleImage = styled.img`
  flex-shrink: 0;
  align-self: center;
  justify-self: center;
  border-radius: ${radius.md};
  /* thumbnail size */
  height: 6rem;
  width: 6rem;
  object-fit: cover;
  font-size: ${fontSize.xs};
`;

export const AlignModuleAndTopic = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${space[1]};
  min-width: 0;
  flex: 1 1 auto;
  align-self: stretch;
`;

export const ModuleHeader = styled.h3`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin: 0;
  font-weight: ${fontWeight.bold};
  font-size: ${fontSize.lg};
  line-height: ${lineHeight.tight};
  color: ${({ theme }) => theme.staffCardHeaderColour};
  overflow-wrap: anywhere;
`;

export const TopicName = styled.p`
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${fontSize.xs};
  font-weight: ${fontWeight.bold};
  line-height: ${lineHeight.tight};
  color: ${({ theme }) => theme.lessonHighlight};
`;

export const AlignProgress = styled(AlignModuleAndTopic)`
  justify-content: start;
  width: 100%;
`;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: ${space[2]};
  flex-shrink: 0;
`;

export const ActiveModule = styled(StaffCommon.StatusChip)``;

export const Index = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.lessonHighlight};
  font-size: ${fontSize.md};
  font-weight: ${fontWeight.bold};
`;

export const AlignSharedIcon = styled.div`
  display: flex;
  align-items: center;
  gap: ${space[2]};
  width: 100%;
  min-height: 2rem;
  color: ${({ theme }) => theme.textDark};

  & > svg {
    flex-shrink: 0;
    margin-left: auto;
  }
`;

export * from '../../common/elements/Notifications';
export * from '../../common/elements/Staff';

const ThumbnailPlaceholder = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 6rem;
  width: 6rem;
  border-radius: ${radius.md};
  background: ${({ theme }) => theme.lessonHighlight};
  color: ${({ theme }) =>
    theme.isLessonDark ? theme.textLight : theme.headerColour};
  font-size: ${fontSize.xxl};
  font-weight: ${fontWeight.heavy};
`;

// shows a lettered tile instead of a broken image when a module has no (working) image
export const ModuleThumbnail = ({ src, name = '' }) => {
  const [hasFailed, setHasFailed] = useState(false);
  if (!src || hasFailed) {
    return (
      <ThumbnailPlaceholder aria-hidden="true">
        {name.trim().charAt(0).toUpperCase()}
      </ThumbnailPlaceholder>
    );
  }
  return (
    <ModuleImage src={src} alt={name} onError={() => setHasFailed(true)} />
  );
};
